// @ts-nocheck

import { GeocodeRequester, type LatLng } from '@cobliteam/api-lib';
import { Haversine } from '@cobliteam/common-types';
import { type GeofenceGeometryPoint, type GeofenceGeometryPolygon, GeofencesDef, GeofenceType } from '@cobliteam/common-types/src/geofenceV3';
import { Environment } from '@cobliteam/react-environments';
import { type AppState, changeDrawLayers } from '@cobliteam/store';
import { colors } from '@cobliteam/ui/theme';
import type L from 'leaflet';
import {
  type FitBoundsOptions,
  type LatLngBoundsExpression,
  type LatLngExpression,
  type Map as LeafletMap,
  Layer as MapLayer,
  type PM,
  type PointExpression,
  type ZoomPanOptions,
} from 'leaflet';
import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styles from './styles';

const FIT_BOUND_PADDING: PointExpression = [100, 100];

const env = Environment.getInstance().getEnvironment();
const geocodeRequester = new GeocodeRequester({ host: env.herbieApiHost });

interface FlyToProps {
  latlng: LatLngExpression;
  zoom?: number;
  options?: ZoomPanOptions;
}

const useMap = () => {
  const map: LeafletMap = useSelector((state: AppState) => state.map.mapRef);
  const drawLayers = useSelector((state: AppState) => state.map.drawLayers);
  const hasDrawLayers = drawLayers.length > 0;
  const dispatch = useDispatch();

  const mapZoom = map?.getZoom();

  const { pointsOnCircle, findMiddlePoint } = Haversine;

  const flyTo = ({ latlng, zoom = 16, options = { duration: 0.5 } }: FlyToProps) => {
    if (map) {
      map.flyTo(latlng, zoom, options);
    }
  };

  const openPopup = useCallback(
    (ref: MapLayer | any, delay = 200) => {
      if (ref instanceof MapLayer) ref.closePopup();

      setTimeout(() => {
        if (ref instanceof MapLayer) {
          ref.openPopup();
          return;
        }

        if (ref && !ref.openPopup()) {
          ref.openPopup();
        }
      }, delay);
    },
    [map]
  );

  const setView = ({ latlng, zoom = 16 }: FlyToProps) => {
    if (map) map.setView(latlng, zoom);
  };

  const setToBounds = (bounds: LatLngBoundsExpression, options?: FitBoundsOptions) => {
    if (Object.keys(bounds).length && map) {
      map.fitBounds(bounds, {
        duration: 0.5,
        padding: FIT_BOUND_PADDING,
        ...options,
      });
    }
  };

  const flyToBounds = (bounds: LatLngBoundsExpression, options?: FitBoundsOptions) => {
    if (Object.keys(bounds).length && map) {
      map.flyToBounds(bounds, {
        duration: 0.5,
        padding: FIT_BOUND_PADDING,
        ...options,
      });
    }
  };

  const getAddressByLatLng = async (latlng: LatLng) => {
    const address = await geocodeRequester.getAddress(latlng);
    return address;
  };

  const parseToGeometryPoint = (latlng: LatLng): GeofenceGeometryPoint => {
    return {
      type: 'Point',
      coordinates: [latlng.lng, latlng.lat],
    };
  };

  const parseToGeometryPolygon = (coordinates: number[][]): GeofenceGeometryPolygon => {
    return {
      type: 'Polygon',
      coordinates: [coordinates],
    };
  };

  const getDrawLayers = () => drawLayers[0];

  const setDrawLayers = (layer: L.Layer) => {
    dispatch(changeDrawLayers([layer]));
  };

  const clearDrawLayers = () => {
    const layer = drawLayers?.[0];
    if (layer) layer.remove();
    dispatch(changeDrawLayers([]));
  };

  const removeDrawLayers = () => {
    if (map) {
      map.eachLayer((layer: L.Layer) => {
        if (layer?.pm?.enabled()) {
          map.removeLayer(layer);
        }
      });
    }
    dispatch(changeDrawLayers([]));
  };

  const bindTooltipInDrawLayers = (title: string) => {
    const layer = getDrawLayers();

    layer
      ?.bindTooltip(title, {
        permanent: true,
        direction: 'bottom',
        sticky: true,
        offset: [0, 10],
        className: styles.tooltip,
      })
      .addEventListener({
        mouseover: () => {
          layer.openTooltip();
        },
        mouseout: () => {
          layer.closeTooltip();
        },
      });
  };

  const unbindTooltipInDrawLayers = () => {
    const layer = getDrawLayers();

    layer?.unbindTooltip();
  };

  const setGeofenceStyle = (type: GeofenceType = GeofenceType.DEFAULT, id = '') => {
    if (!(type && map)) return;
    const color = GeofencesDef[type].cssColor;

    const drawOptions: PM.DrawModeOptions = {
      templineStyle: { color },
      hintlineStyle: {
        color: colors.gray[10],
        stroke: true,
        dashArray: [5, 5],
      },
      pathOptions: { color },
    };

    map.pm.setLang('pt_br');
    map.pm.setGlobalOptions(drawOptions);

    const layerGroup = map.pm.getGeomanDrawLayers(true);
    const layer = getDrawLayers();

    if (layer) {
      if (layerGroup.getLayers().length) layerGroup.setStyle({ color, className: styles.layer(color) });
      // Guard against layers that have been detached from the map (layer._map === null),
      // which can happen when stale draw layers remain in the Redux store after a tab switch.
      // Leaflet's Layer.getPane internally calls this._map.getPane(name) and would throw.
      if (layer?.getPane && layer._map) {
        const layerPath = layer?.getPane?.('overlayPane')?.getElementsByClassName(id) ?? [];

        // This is a workaround to remove the last class added in the layer
        const element = layerPath?.[0];

        if (element) {
          // remove all class
          element.removeAttribute('class');

          // add class to element
          element.classList.add('leaflet-interactive');
          element.classList.add(id);
          element.classList.add(styles.layer(color));
        }
      }
    }
  };

  const setTooltipStyle = () => {
    // map?.pm?.setLang('pt_br', customTranslation, 'en');
    const layer = getDrawLayers();
    // Same detached-layer guard as above to avoid `this._map` being null inside getPane.
    if (layer?._map) {
      const tooltip = layer?.getPane?.('tooltipPane');
      if (tooltip) tooltip.setAttribute('class', `${tooltip.className} ${styles.tooltip}`);
    }
  };

  return {
    map,
    mapZoom,
    hasDrawLayers,
    drawLayers,
    flyTo,
    flyToBounds,
    setView,
    setToBounds,
    openPopup,
    pointsOnCircle,
    parseToGeometryPoint,
    parseToGeometryPolygon,
    getAddressByLatLng,
    bindTooltipInDrawLayers,
    unbindTooltipInDrawLayers,
    setGeofenceStyle,
    setTooltipStyle,
    getDrawLayers,
    setDrawLayers,
    clearDrawLayers,
    removeDrawLayers,
    findMiddlePoint,
  };
};

export default useMap;
